import type { Container } from 'pixi.js';
import type { MainMenuViewConfig, MenuPage } from './MainMenuView';
import { MainMenuView } from './MainMenuView';
import { PageType } from './types';
import type { App } from '../app/App';
import type { DioramaView } from '../game/views/dioramas/DioramaView';
import { IAPPlacement } from '../app/iap/types';
import { soundManager } from '../app/SoundManager';
import { TournamentPageView } from './tournamentPage/TournamentPageView';
import { TournamentCategory } from '../app/tournaments/TournamentService';
import { DummyPageView } from './DummyPageView';
import { showSettingsMenu } from '../behaviours/showSettingsMenu';
import { HomePageController } from './HomePageController';
import { openOutOfLivesPopup } from '../behaviours/openOutOfLivesPopup';
import { openShopPopup } from '../behaviours/openShopPopup';
import { findOfferAndOpenPopup } from '../behaviours/findOfferAndOpenPopup';
import { OfferLocation } from '../app/offers/offersTypes';
import { openForceUpdatePopup } from '../behaviours/openForceUpdatePopup';
import { openNotificationPermissionPopup } from '../behaviours/openNotificationPermissionPopup';
import { openRateUsPopupForFirstDiorama } from '../behaviours/openRateUsPopup';

type MainMenuControllerConfig = {
  app: App;
  root: Container;
  goToNextLevel: () => void;
  restartScene: () => void;
};

export class MainMenuController {
  public mainMenuView: MainMenuView | null = null;

  private homePageController: HomePageController | null = null;

  constructor(private readonly config: MainMenuControllerConfig) { }

  public async playButtonAction(): Promise<void> {
    const {
      mainMenuView,
      config: {
        goToNextLevel,
        app: { stateController },
      },
    } = this;
    if (!mainMenuView) return;

    if (stateController.getLives() === 0) {
      await openOutOfLivesPopup({
        app: this.config.app,
        entryPoint: 'main-menu-play-button',
        viewsToMoveAbove: [mainMenuView.topBar.livesCounter],
      });
      return;
    }

    void this.hideBannerAd();
    goToNextLevel();
    mainMenuView.interactiveChildren = false;
    await mainMenuView.close();
  }

  public openMainMenu(dioramaView?: DioramaView): { closeMenu: () => void } {
    const {
      app: { stateController, iap },
      root,
    } = this.config;

    const menuView = new MainMenuView({
      state: stateController,
      pages: this.createPages(dioramaView),
      activePage: PageType.Home,
      iapAvailable: !!iap,
    }).props({
      parent: root,
      addAtIndex: 0,
    });
    this.mainMenuView = menuView;

    menuView.onTabButtonClicked.connect((tab) => {
      menuView.selectPage(tab);
    });

    menuView.topBar.moneyCounter.onClicked.connect(() => {
      if (iap) menuView.selectPage(PageType.Store);
    });

    menuView.topBar.livesCounter.onClicked.connect(() => {
      void openOutOfLivesPopup({
        app: this.config.app,
        entryPoint: 'lives-counter',
        viewsToMoveAbove: [menuView.topBar.livesCounter],
      });
      soundManager.play('UI_Button_Click');
    });

    menuView.topBar.settingsButton.onpointertap = (): void => {
      this.showSettingsPopup();
      soundManager.play('UI_Button_Click');
    };

    stateController.subscriptions.onChange(
      ['freeShopGift'],
      () => {
        menuView.toggleNotificationOnPage(PageType.Store, stateController.hasFreeShopGiftToOpen());
      },
      this,
    );

    // Show banner ads if user is not a payer
    void this.showBannerAdIfAllowed();

    // Hook into popup events to hide/show banner when popups open/close
    this.setupPopupBannerHandling();

    void this.startOpenMainMenuFlow();

    // Check for force update after main menu is initialized
    // Use setTimeout to ensure it runs after other initialization is complete
    setTimeout(() => {
      if (this.config.app.platform.forceUpdate?.shouldShowUpdatePopup() ?? false) {
        openForceUpdatePopup(this.config.app);
      }
    }, 800); // 800ms delay to ensure main menu is fully loaded

    return {
      closeMenu: (): void => {
        void this.hideBannerAd();
        void menuView.close();
      },
    };
  }

  private createPages(dioramaView?: DioramaView): MainMenuViewConfig['pages'] {
    const {
      app: { iap, stateController, tournamentService },
    } = this.config;

    const pages: MainMenuViewConfig['pages'] = [];

    this.homePageController = new HomePageController({
      app: this.config.app,
      mainMenuController: this,
      dioramaView,
    });

    const homePageView = this.homePageController.getHomePageView();

    if (iap) {
      pages.push({
        type: PageType.Store,
        page: null,
        locked: false,
        hasNotification: stateController.hasFreeShopGiftToOpen(),
        onPageButtonClicked: () => {
          void openShopPopup(this.config.app, IAPPlacement.Shop);
        },
      });
    }

    pages.push({ type: PageType.Home, page: homePageView, locked: false, hasNotification: false });

    if (tournamentService) {
      pages.push({
        type: PageType.Tournament,
        page: this.createTournamentView(() => {
          void this.playButtonAction();
        }),
        locked: false,
        hasNotification: false,
      });
    }

    pages.push({
      type: PageType.Settings,
      page: null,
      locked: false,
      hasNotification: false,
      onPageButtonClicked: () => {
        this.showSettingsPopup();
      },
    });

    return pages;
  }

  private createTournamentView(goToNextLevel: () => void): MenuPage {
    const { app } = this.config;
    const { tournamentService, stateController } = app;

    if (!tournamentService) {
      return new DummyPageView();
    }

    const view = new TournamentPageView({
      state: stateController,
      activeTournament: tournamentService.getCurrentTournament(),
      currentUser: app.platform.getCurrentPlayer(),
      tournamentGetter: (category: TournamentCategory) => tournamentService.getTournamentsByCategory(category),
    });

    view.setTab(TournamentCategory.Global);
    view.onTabClicked.connect((tab) => {
      view.setTab(tab);
    });

    view.onTournamentJoinClicked.connect(async (tournamentId) => {
      const success = await tournamentService.joinTournament(tournamentId);
      if (success) goToNextLevel();
    });

    view.onTournamentCreateClicked.connect(async () => {
      const success = await tournamentService.createTournament();
      if (success) goToNextLevel();
    });

    return view;
  }

  private showSettingsPopup(): void {
    showSettingsMenu(this.config.app, {
      onLanguageChanged: this.config.restartScene,
    });
  }

  private async startOpenMainMenuFlow(): Promise<void> {
    await this.homePageController?.onAnimationFinished;
    const {
      config: {
        app: { gameEvents, stateController, controller },
      },
    } = this;

    if (!this.isActive()) return;

    if (!this.mainMenuView) return;

    gameEvents.setRewardTargets(
      this.mainMenuView.getCoinCounter(),
      this.homePageController?.getHomePageView()?.getPlayButton(),
    );
    gameEvents.setPlayLevelAction(async () => {
      await this.playButtonAction();
    });
    gameEvents.setToggleCoinCounterUpdate((value: boolean, checkForUpdate = false) => {
      const coinCounter = this.mainMenuView?.getCoinCounter();

      if (coinCounter) {
        const needsUpdate = checkForUpdate && !coinCounter.updateEnabled && value;

        coinCounter.updateEnabled = value;

        if (needsUpdate) {
          coinCounter.updateAmount(stateController.getCoins(), true);
        }
      }
    });

    if (stateController.getCurrentDioramaState().dioramaIndex > 0) {
      await controller.popups.waitUntilNoActivePopups();
      await openRateUsPopupForFirstDiorama(this.config.app);
    }

    await gameEvents.startMainMenuFlow(this.mainMenuView);

    if (this.mainMenuView.destroyed) return;

    await findOfferAndOpenPopup(this.config.app, OfferLocation.MenuPopup, null, IAPPlacement.MainMenu);

    // Show notification permission popup after all event flows complete
    // This ensures it appears after win streak, race events, season pass, and offers
    if (!this.mainMenuView?.destroyed) {
      await this.config.app.controller.popups.waitUntilNoActivePopups();
      void openNotificationPermissionPopup(this.config.app);
    }
  }

  private isActive(): boolean {
    return !!(this.mainMenuView && !this.mainMenuView.destroyed);
  }

  /**
   * Check if banner ads should be shown
   * Returns false if user is a payer or if ads are not available
   */
  private shouldShowBannerAd(): boolean {
    const { app } = this.config;
    
    // Don't show banner if user is a payer
    if (app.iap?.isPayer()) {
      return false;
    }

    return true;
  }

  /**
   * Show banner ad if allowed (user is not a payer)
   */
  private async showBannerAdIfAllowed(): Promise<void> {
    if (!this.shouldShowBannerAd()) {
      return;
    }

    try {
      await this.config.app.platform.showBannerAd();
    } catch (error) {
      console.error('[MainMenuController] Failed to show banner ad:', error);
    }
  }

  /**
   * Hide banner ad
   */
  private async hideBannerAd(): Promise<void> {
    try {
      await this.config.app.platform.hideBannerAd();
    } catch (error) {
      console.error('[MainMenuController] Failed to hide banner ad:', error);
    }
  }

  /**
   * Setup banner ad handling for popup open/close events
   * Hides banner when popups open, shows it again when all popups close
   */
  private setupPopupBannerHandling(): void {
    const { popups } = this.config.app.controller;
    let bannerWasVisible = false;

    // Hide banner when popup opens
    popups.onPopupAdded.connect(() => {
      // Only hide if banner is currently visible and we're in main menu
      if (this.mainMenuView && !this.mainMenuView.destroyed && this.shouldShowBannerAd()) {
        bannerWasVisible = true;
        void this.hideBannerAd();
      }
    });

    // Show banner when popup closes (if no other popups are open)
    popups.onPopupRemoved.connect(() => {
      // Only show if no popups are open, we're in main menu, and banner was visible before
      if (
        !popups.isAnyPopupOpened() &&
        this.mainMenuView &&
        !this.mainMenuView.destroyed &&
        bannerWasVisible &&
        this.shouldShowBannerAd()
      ) {
        bannerWasVisible = false;
        void this.showBannerAdIfAllowed();
      }
    });
  }
}
